import React, { useEffect, useId, useRef } from 'react';
import { IconClose, IconTrash } from '../icons';
import useFocusTrap from '../hooks/useFocusTrap';

const DeleteModal = ({ isOpen, onClose, onConfirm, itemType, itemName }) => {
  const dialogRef = useRef(null);
  const titleId = useId();

  // Keep the page from scrolling behind the dialog; restore whatever was set
  // before, since a drawer underneath may already have locked it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useFocusTrap(dialogRef, { isOpen, onEscape: onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-overlay flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // The top padding grows on mobile because the close button grows to
        // 44x44 there and would otherwise overlap the heading.
        className="relative bg-card rounded-xl w-full max-w-[420px] pt-[42px] max-md:pt-[58px] pb-4 px-4 flex flex-col items-center gap-2.5 shadow-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-7 h-7 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-full text-ink-muted hover:text-ink"
        >
          <IconClose size={16} />
        </button>

        <div className="flex flex-col items-center gap-1 text-center w-full pb-4">
          <p id={titleId} className="text-[18px] leading-[22px] font-medium text-ink">
            Delete {itemType} {itemName ? `“${itemName}”` : ''}?
          </p>
          <p className="text-sm leading-[18px] text-ink-muted">
            Once permanently deleted, this {itemType} cannot be restored. The data will be deleted forever.
          </p>
        </div>

        <div className="flex gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-brandFill hover:bg-brandFillHover text-white rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-surface hover:bg-line/60 text-danger-ink rounded-lg px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            <IconTrash size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal;
